import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    children: React.ReactElement<any> | React.ReactNode;
    open: boolean;
    transition?: string;
}
/*
1. Set height to 'auto' in the end when it's open so that it's responsive by default.
2. No matter is open or close, we need to first set the height to a number, so that we can trigger the transition.

If 300px is the element's height:
From open to close : 'auto' -> 300 -> 0.
From close to open: 0 -> 300 -> 'auto'
*/
const CollapseItem = ({
    className,
    children,
    open,
    transition = "height .3s ease-out",
    ...rest
}: Props) => {
    const mounted = useRef(false);
    const ref = useRef<HTMLDivElement>(null);
    const [isCollapaseClosing, setIsCollapaseClosing] = useState(false);
    const [height, setHeight] = useState(open ? "auto" : 0);

    function getContainerHeight() {
        if (ref && ref.current) {
            return ref.current.scrollHeight;
        }
        return 0;
    }

    function onTransitionEnd(e: React.TransitionEvent<HTMLDivElement>) {
        // transition could be trigger by other elements.
        if (e.target !== ref.current || !open) {
            return;
        }
        setHeight("auto");
    }

    useEffect(() => {
        if (mounted.current) {
            // change the element's height from 'auto' to actual height so that we can trigger the animation.
            setHeight(getContainerHeight());
            if (!open) {
                setIsCollapaseClosing(true);
            }
        }
        if (!mounted.current) {
            mounted.current = true;
        }
    }, [open]);

    useEffect(() => {
        // start the animation to close the collapase.
        if (isCollapaseClosing && height === getContainerHeight()) {
            setIsCollapaseClosing(false);
            setHeight(0);
        }
    }, [isCollapaseClosing, height]);
    return (
        <div
            style={{
                height,
                transition,
                overflow: "hidden",
                willChange: "height",
            }}
            aria-hidden={!open}
            className={classNames(className)}
            onTransitionEnd={onTransitionEnd}
            ref={ref}
            {...rest}
        >
            <div>{children}</div>
        </div>
    );
};

// export it this way for future proof development
// because in the future there will be "CollapsePanel" and "CollapseContainer"
export default {
    CollapseItem,
};
